import React, { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PenLine, Eraser, CheckCircle2, Trash2 } from "lucide-react";
import { useBlobImage } from "@/hooks/useBlobImage";
import api from "@/lib/api";

const FIELDS = [
  { key: "nome", label: "Nome e cognome tecnico", ph: "Ing. Mario Rossi", testid: "firma-nome-input" },
  { key: "titolo", label: "Titolo professionale", ph: "Geometra / Architetto / Ingegnere", testid: "firma-titolo-input" },
  { key: "albo", label: "N. iscrizione albo", ph: "Es. 1234 - Ordine di Cagliari", testid: "firma-albo-input" },
  { key: "luogo", label: "Luogo", ph: "Cagliari", testid: "firma-luogo-input" },
];

export const SignaturePad = ({ pratica }) => {
  const qc = useQueryClient();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [form, setForm] = useState({ nome: "", titolo: "", albo: "", luogo: "", data: new Date().toISOString().slice(0, 10) });
  const firma = pratica.firma;
  const firmaUrl = useBlobImage(firma?.storage_path);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0F172A";
  }, [firma]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (canvasRef.current.width / r.width), y: (src.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasInk(true); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); };

  const save = useMutation({
    mutationFn: async () => (await api.put(`/pratiche/${pratica.id}/firma`, { ...form, signature_png: canvasRef.current.toDataURL("image/png") })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pratica", pratica.id] }); toast.success("Esito firmato e datato"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore nel salvataggio della firma"),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/pratiche/${pratica.id}/firma`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pratica", pratica.id] }); toast.success("Firma rimossa"); },
  });

  return (
    <div data-testid="signature-card" className="rounded-xl border border-slate-800 bg-[#121824] p-5">
      <h3 className="font-heading font-semibold flex items-center gap-2 mb-1"><PenLine className="h-4 w-4 text-cyan-400" /> Firma Asseverata dell'Esito</h3>
      <p className="text-xs text-slate-500 mb-4">Il tecnico firma e data l'esito; la firma viene allegata alla pratica e al report PDF.</p>

      {firma ? (
        <div data-testid="signature-saved" className="flex items-center gap-4 p-4 rounded-lg bg-[#0B0F17] border border-emerald-500/30">
          {firmaUrl && <img src={firmaUrl} alt="firma" className="h-14 bg-white rounded px-2" />}
          <div className="flex-1 min-w-0 text-sm">
            <p className="flex items-center gap-1.5 text-emerald-400 font-medium"><CheckCircle2 className="h-4 w-4" /> Firmato da {firma.nome}</p>
            <p className="text-slate-400 text-xs mt-0.5">{[firma.titolo, firma.albo && `Albo n. ${firma.albo}`].filter(Boolean).join(" · ")}</p>
            <p className="text-slate-500 text-xs">{firma.luogo} {firma.data} · esito {firma.esito_firmato} ({firma.discrepancy_firmata}%)</p>
          </div>
          <button data-testid="signature-delete-btn" onClick={() => remove.mutate()} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider">{f.label}</label>
                <input data-testid={f.testid} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.ph}
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm" />
              </div>
            ))}
            <div>
              <label className="text-[11px] text-slate-500 uppercase tracking-wider">Data</label>
              <input data-testid="firma-data-input" type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm" />
            </div>
          </div>
          <div className="relative">
            <canvas ref={canvasRef} data-testid="signature-canvas" width={700} height={200}
              className="w-full h-[150px] bg-white rounded-lg border border-slate-600 cursor-crosshair touch-none"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
            {!hasInk && <span className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm pointer-events-none">Firma qui con mouse o dito</span>}
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="signature-clear-btn" onClick={clear} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1A2332] border border-slate-700 text-sm"><Eraser className="h-4 w-4" /> Cancella</button>
            <button data-testid="signature-save-btn" disabled={!hasInk || !form.nome.trim() || save.isPending} onClick={() => save.mutate()}
              className="flex-1 px-3 py-2 rounded-lg bg-cyan-500 text-slate-900 text-sm font-semibold disabled:opacity-40">Firma e data l'esito</button>
          </div>
        </>
      )}
    </div>
  );
};
